// useIsMobile / useReadOnly — 手機版唯讀模式 (Phase 1G)。
//
// User spec 2026-05-10: 「手機版本只能 view 跟處理工作流程, 不能新建或是修改資料」。
//
// 設計:
//   · 全 app 用 useIsMobile() 判斷 viewport, 屬於 mobile (< md = 768px) 即
//     等於 readOnly = true。
//   · 任何 mutation UI ("+ 新增 / ✏️ 編輯 / 刪除 / StatusEditor / 申請 /
//     drawer 編輯模式") 都要 wrap `{!readOnly && ...}` 隱藏掉。
//   · 例外 1: 鈴鐺 + /workflows drawer 的「✓ 核准 / ✕ 退回 / ✕ 取消」必須留著
//     — 推進工作流程不算 entity mutation, 是 user spec 明確允許的。
//   · 例外 2 (2026-08-08 新增): **建立簽核單的三個入口留著** —— /approvals 的
//     「+ 新增申請」、/payables 的「+ 付款申請」、/leave 的「+ 申請請假」。
//     上面那行把「申請」列為要隱藏的 mutation UI, 但那是 2026-05-10 訂的, 當時
//     還沒有簽核模組。 送一張簽核單就是「處理工作流程」(spec 明確允許), 而且
//     **用手機拍收據直接報帳正是行動裝置最該做的事**; 實測報帳抽屜在 375px 是
//     滿版、零溢出、完全可用, 不是空頭承諾。 要改回全擋的話, 記得同時改上面
//     那行, 別讓規範跟現況再次對不上。
//   · 例外 3 (2026-08-22 新增): **列表上的 🔒「申請加入基金/專案」留著**
//     (components/AccessLockButton.tsx)。 同樣的理由 —— 它建立的是一張
//     workflow_instance, spec 明確允許「處理工作流程」。 原本手機整顆隱藏的結果是:
//     member 點鎖住的列沒有任何反應、也沒有任何說明, 連「你不是這個基金的成員」
//     都看不到 (那句話只在 🔒 的 hover tooltip 裡, 而手機沒有 hover)。
//   · 後端不擋 — 純前端 UX gate。 想要真正硬擋, 後端可加 actor type check
//     從 User-Agent 或 X-Sanshi-Mobile header 拒絕 mutation, 但 Phase 1
//     不做 (內部 tenant, 不需要強保護)。
//
// 注意: window.matchMedia 在 SSR 沒有, 我們是 SPA (Vite + React) 所以 OK,
// 但仍寫成 lazy state initializer + cleanup 防範未來搬到 SSR。

import { useEffect, useState } from 'react';

const MOBILE_BREAKPOINT = '(max-width: 767px)';

export function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_BREAKPOINT).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia(MOBILE_BREAKPOINT);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

/** mobile === read-only (view + workflow decide only). Phase 1G policy. */
export function useReadOnly(): boolean {
  return useIsMobile();
}
